import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet } from '../../api/apiClient';
import type { MarketSearchResponse, SearchEvent } from './searchTypes';

export interface MarketSearchResult {
    events: SearchEvent[];
    response: MarketSearchResponse | null;
    loading: boolean;
    error: string | null;
    total: number;
    pageIndex: number;
    hasPrev: boolean;
    hasNext: boolean;
    nextPage: () => void;
    prevPage: () => void;
}

export function useMarketSearch(query: string | null): MarketSearchResult {
    const [response, setResponse] = useState<MarketSearchResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [stack, setStack] = useState<(string | null)[]>([null]);
    const callIdRef = useRef(0);

    useEffect(() => {
        setStack([null]);
        setResponse(null);
    }, [query]);

    const cursor = stack[stack.length - 1] ?? null;

    useEffect(() => {
        if (!query) {
            callIdRef.current++;
            setLoading(false);
            return;
        }
        const callId = ++callIdRef.current;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        const url = cursor
            ? `/api/markets/search?${query}&cursor=${encodeURIComponent(cursor)}`
            : `/api/markets/search?${query}`;

        apiGet<MarketSearchResponse>(url, controller.signal)
            .then(res => {
                if (callId !== callIdRef.current) return;
                setResponse(res);
            })
            .catch(err => {
                if (err?.name === 'AbortError' || callId !== callIdRef.current) return;
                setError(err instanceof Error ? err.message : String(err));
                setResponse(null);
            })
            .finally(() => {
                if (callId !== callIdRef.current) return;
                setLoading(false);
            });

        return () => {
            callIdRef.current++;
            controller.abort();
        };
    }, [query, cursor]);

    const nextPage = useCallback(() => {
        const next = response?.next_cursor;
        if (next) setStack(prev => [...prev, next]);
    }, [response?.next_cursor]);

    const prevPage = useCallback(() => {
        setStack(prev => (prev.length > 1 ? prev.slice(0, -1) : prev));
    }, []);

    const events = useMemo(() => response?.events ?? [], [response]);

    return {
        events,
        response,
        loading,
        error,
        total: response?.total_events ?? 0,
        pageIndex: stack.length - 1,
        hasPrev: stack.length > 1,
        hasNext: Boolean(response?.next_cursor),
        nextPage,
        prevPage,
    };
}